import React, { useState, useEffect, useCallback } from 'react';  
import { 
  FiUser, FiBell, FiShield, FiGlobe, FiSave, FiKey, 
  FiCheck, FiLoader, FiEye, FiEyeOff, FiSmartphone, FiMail, FiClock,
  FiSettings as FiSettingsIcon
} from 'react-icons/fi';
import { settingsApi } from '../services/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    // Profile
    timezone: 'UTC',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    // UI
    compactView: false,
    itemsPerPage: 10,
    // Security
    twoFactorEnabled: false,
    sessionTimeout: '30'
  });

  // Password state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Reference data
  const [timezones, setTimezones] = useState([]);
  const [languages, setLanguages] = useState([]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsData, timezonesData, languagesData] = await Promise.all([
        settingsApi.get(1),
        settingsApi.getTimezones(),
        settingsApi.getLanguages()
      ]);
      
      setSettings({
        timezone: settingsData.timezone || 'UTC',
        language: settingsData.language || 'en',
        dateFormat: settingsData.dateFormat || 'MM/DD/YYYY',
        emailNotifications: settingsData.emailNotifications ?? true,
        pushNotifications: settingsData.pushNotifications ?? true,
        assessmentAlerts: settingsData.assessmentAlerts ?? true,
        deadlineReminders: settingsData.deadlineReminders ?? true,
        weeklyDigest: settingsData.weeklyDigest ?? false,
        securityAlerts: settingsData.securityAlerts ?? true,
        compactView: settingsData.compactView ?? false,
        itemsPerPage: settingsData.itemsPerPage ?? 10,
        twoFactorEnabled: settingsData.twoFactorEnabled ?? false,
        sessionTimeout: settingsData.sessionTimeout || '30'
      });
      
      setTimezones(timezonesData);
      setLanguages(languagesData);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      // Use defaults if API fails
      setTimezones([{ id: 'UTC', name: 'UTC' }]);
      setLanguages([{ code: 'en', name: 'English' }]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await settingsApi.update(1, settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    
    setSaving(true);
    try {
      await settingsApi.changePassword(1, passwordForm);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password changed successfully');
    } catch (err) {
      console.error('Failed to change password:', err);
      alert('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile & Locale', icon: FiUser },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'appearance', label: 'Appearance', icon: FiSettingsIcon },
    { id: 'security', label: 'Security', icon: FiShield },
  ];

  const Toggle = ({ enabled, onChange }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-[#ff6600]' : 'bg-slate-300'
      }`}
    >
      <span
        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          enabled ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiLoader className="w-8 h-8 animate-spin text-blue-500" />
        <span className={`ml-2 text-slate-600`}>Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold text-slate-800`}>Settings</h1>
          <p className={`mt-1 text-slate-500`}>Manage your account preferences and security</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff6600] text-white rounded-lg text-sm font-medium hover:bg-[#e65c00] transition-colors disabled:opacity-50"
        >
          {saving ? <FiLoader className="animate-spin" /> : saveSuccess ? <FiCheck /> : <FiSave />}
          {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <div className={`lg:w-64 rounded-xl border p-4 bg-white border-slate-200`}>
          <nav className="space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#ff6600] text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className={`flex-1 rounded-xl border p-6 bg-white border-slate-200`}>
          {/* Profile & Locale Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h2 className={`text-lg font-semibold mb-4 text-slate-800`}>
                  <FiGlobe className="inline-block mr-2" />
                  Locale Settings
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Timezone</label>
                    <select
                      value={settings.timezone}
                      onChange={(e) => handleSettingChange('timezone', e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    >
                      {timezones.map(tz => (
                        <option key={tz.id} value={tz.id}>{tz.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Language</label>
                    <select
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    >
                      {languages.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Date Format</label>
                    <select
                      value={settings.dateFormat}
                      onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Items Per Page</label>
                    <select
                      value={settings.itemsPerPage}
                      onChange={(e) => handleSettingChange('itemsPerPage', Number(e.target.value))}
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className={`text-lg font-semibold text-slate-800`}>
                <FiBell className="inline-block mr-2" />
                Notification Preferences
              </h2>

              <div className="space-y-4">
                <div className={`flex items-center justify-between p-4 rounded-lg bg-slate-50`}>
                  <div className="flex items-center gap-3">
                    <FiMail className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Email Notifications</p>
                      <p className={`text-sm text-slate-500`}>Receive notifications via email</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.emailNotifications} onChange={(v) => handleSettingChange('emailNotifications', v)} />
                </div>

                <div className={`flex items-center justify-between p-4 rounded-lg bg-slate-50`}>
                  <div className="flex items-center gap-3">
                    <FiSmartphone className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Push Notifications</p>
                      <p className={`text-sm text-slate-500`}>Receive push notifications in browser</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.pushNotifications} onChange={(v) => handleSettingChange('pushNotifications', v)} />
                </div>

                <div className={`flex items-center justify-between p-4 rounded-lg bg-slate-50`}>
                  <div className="flex items-center gap-3">
                    <FiSettingsIcon className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Assessment Alerts</p>
                      <p className={`text-sm text-slate-500`}>Get notified about assessment updates</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.assessmentAlerts} onChange={(v) => handleSettingChange('assessmentAlerts', v)} />
                </div>

                <div className={`flex items-center justify-between p-4 rounded-lg bg-slate-50`}>
                  <div className="flex items-center gap-3">
                    <FiClock className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Deadline Reminders</p>
                      <p className={`text-sm text-slate-500`}>Get reminded before deadlines</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.deadlineReminders} onChange={(v) => handleSettingChange('deadlineReminders', v)} />
                </div>

                <div className={`flex items-center justify-between p-4 rounded-lg bg-slate-50`}>
                  <div className="flex items-center gap-3">
                    <FiMail className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Weekly Digest</p>
                      <p className={`text-sm text-slate-500`}>Receive a weekly summary email</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.weeklyDigest} onChange={(v) => handleSettingChange('weeklyDigest', v)} />
                </div>

                <div className={`flex items-center justify-between p-4 rounded-lg bg-slate-50`}>
                  <div className="flex items-center gap-3">
                    <FiShield className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Security Alerts</p>
                      <p className={`text-sm text-slate-500`}>Get notified about security events</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.securityAlerts} onChange={(v) => handleSettingChange('securityAlerts', v)} />
                </div>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <h2 className={`text-lg font-semibold text-slate-800`}>
                <FiSettingsIcon className="inline-block mr-2" />
                Appearance Settings
              </h2>

              <div className="space-y-4">
                <div className={`flex items-center justify-between p-4 rounded-lg bg-slate-50`}>
                  <div className="flex items-center gap-3">
                    <FiSettingsIcon className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Compact View</p>
                      <p className={`text-sm text-slate-500`}>Use smaller spacing and fonts</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.compactView} onChange={(v) => handleSettingChange('compactView', v)} />
                </div>
              </div>

              {/* Theme Preview */}
              <div>
                <h3 className={`text-sm font-medium mb-3 text-slate-700`}>Preview</h3>
                <div className={`p-4 rounded-lg border bg-white border-slate-200`}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 bg-[#0b79b8] rounded-lg"></div>
                    <div className="flex-1">
                      <div className={`h-3 w-32 rounded bg-slate-200`}></div>
                      <div className={`h-2 w-48 rounded mt-2 bg-slate-100`}></div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className={`h-8 w-20 rounded bg-slate-100`}></div>
                    <div className={`h-8 w-20 rounded bg-slate-100`}></div>
                    <div className="h-8 w-20 rounded bg-[#ff6600]"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h2 className={`text-lg font-semibold text-slate-800`}>
                <FiShield className="inline-block mr-2" />
                Security Settings
              </h2>

              {/* Two-Factor Authentication */}
              <div className={`p-4 rounded-lg bg-slate-50`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FiSmartphone className={`w-5 h-5 text-slate-500`} />
                    <div>
                      <p className={`font-medium text-slate-800`}>Two-Factor Authentication</p>
                      <p className={`text-sm text-slate-500`}>Add an extra layer of security</p>
                    </div>
                  </div>
                  <Toggle enabled={settings.twoFactorEnabled} onChange={(v) => handleSettingChange('twoFactorEnabled', v)} />
                </div>
              </div>

              {/* Session Timeout */}
              <div>
                <label className={`block text-sm font-medium mb-2 text-slate-700`}>Session Timeout (minutes)</label>
                <select
                  value={settings.sessionTimeout}
                  onChange={(e) => handleSettingChange('sessionTimeout', e.target.value)}
                  className={`w-full md:w-64 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="120">2 hours</option>
                  <option value="480">8 hours</option>
                </select>
              </div>

              {/* Change Password */}
              <div className={`border-t pt-6 border-slate-200`}>
                <h3 className={`text-md font-semibold mb-4 text-slate-800`}>
                  <FiKey className="inline-block mr-2" />
                  Change Password
                </h3>
                
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Current Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className={`w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-500`}
                      >
                        {showPasswords.current ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        className={`w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-500`}
                      >
                        {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className={`w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-500`}
                      >
                        {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                    className="px-5 py-2.5 bg-[#ff6600] text-white rounded-lg text-sm font-medium hover:bg-[#e65c00] transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
